// iter134 - ChunkLoadError boundary.
// Browsers cache the old index.html with hashed chunk filenames; after a
// redeploy, those hashed filenames 404. React's Suspense never resolves and
// the user sees a blank screen until a hard refresh. The textbook fix is to
// catch the ChunkLoadError and force a one-shot reload that picks up the
// new hashed chunk names.
//
// Safety: we only auto-reload once per browser session via sessionStorage
// so we never enter an infinite loop on truly broken builds.
import React from "react";

const SESSION_KEY = "sl_chunk_reload_v1";

function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed")
  );
}

export default class ChunkErrorBoundary extends React.Component {
  state = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      try {
        const already = sessionStorage.getItem(SESSION_KEY);
        if (!already) {
          sessionStorage.setItem(SESSION_KEY, "1");
          // small delay so React flushes the boundary UI first
          setTimeout(() => window.location.reload(), 80);
        }
      } catch {
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div
            data-testid="chunk-reload-banner"
            style={{
              padding: "40px 20px", textAlign: "center", fontFamily: "Inter, sans-serif",
              minHeight: "60vh", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 600, color: "#0D3D6B" }}>
              Refreshing to load the latest version…
            </div>
            <div style={{ fontSize: 13, color: "#5C635E" }}>
              This usually takes less than a second.
            </div>
          </div>
        );
      }
      return (
        <div style={{ padding: "40px 20px", textAlign: "center", fontFamily: "Inter, sans-serif" }} data-testid="generic-error-banner">
          <div style={{ fontSize: 18, fontWeight: 600, color: "#0D3D6B", marginBottom: 8 }}>Something went wrong.</div>
          <div style={{ fontSize: 13, color: "#5C635E", marginBottom: 16 }}>Please reload the page.</div>
          <button
            onClick={() => { try { sessionStorage.removeItem(SESSION_KEY); } catch {} window.location.reload(); }}
            style={{ padding: "10px 18px", background: "#0D3D6B", color: "white", border: "none", borderRadius: 8, cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
